import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

const TIP_GAP_PX = 8;
const TIP_VIEWPORT_PAD_PX = 8;
const TIP_ESTIMATED_WIDTH_PX = 220;
const TIP_ESTIMATED_HEIGHT_PX = 72;

/**
 * Viewport-fixed coordinates for the send-confirm tooltip.
 *
 * Prefer below the Send button (so the tip does not cover Copy / Send / Try
 * again). Flip above when the space under the button is too tight. Right-align
 * to the button, then clamp so the whole tip — including Confirm — stays on
 * screen. Used with a body portal so overflow:auto ancestors cannot clip it.
 *
 * @example
 * computeSendConfirmTipStyle(
 *   { getBoundingClientRect: () => ({ top: 100, bottom: 124, left: 200, right: 248 }) },
 *   { offsetHeight: 70, offsetWidth: 180 }
 * );
 * // { top: 132, left: 68, openAbove: false }
 */
export const computeSendConfirmTipStyle = (anchorEl, tipEl) => {
  if (!anchorEl) return null;

  const rect = anchorEl.getBoundingClientRect();
  const tipHeight = tipEl?.offsetHeight || TIP_ESTIMATED_HEIGHT_PX;
  const tipWidth = tipEl?.offsetWidth || TIP_ESTIMATED_WIDTH_PX;
  const viewportW = window.innerWidth;
  const viewportH = window.innerHeight;
  const spaceBelow = viewportH - rect.bottom - TIP_GAP_PX;
  const spaceAbove = rect.top - TIP_GAP_PX;
  const openAbove = spaceBelow < tipHeight && spaceAbove > spaceBelow;

  let top = openAbove ? rect.top - TIP_GAP_PX - tipHeight : rect.bottom + TIP_GAP_PX;
  top = Math.min(
    Math.max(TIP_VIEWPORT_PAD_PX, top),
    Math.max(TIP_VIEWPORT_PAD_PX, viewportH - tipHeight - TIP_VIEWPORT_PAD_PX)
  );

  let left = rect.right - tipWidth;
  if (left + tipWidth > viewportW - TIP_VIEWPORT_PAD_PX) {
    left = viewportW - tipWidth - TIP_VIEWPORT_PAD_PX;
  }
  if (left < TIP_VIEWPORT_PAD_PX) {
    left = TIP_VIEWPORT_PAD_PX;
  }

  return { top, left, openAbove };
};

/**
 * Quiet send confirm as a tooltip on the Send button — the toolbar does not move.
 *
 * First click opens a small popover on the button with the destination. Confirm
 * in the tooltip (or a second click on Send) actually sends. Click outside or
 * Escape dismisses. The tip is portaled to ``document.body`` so a scrolling
 * details pane (web and Electron) cannot clip Confirm off the clickable area.
 *
 * @example
 * <InlineSendConfirm hint="Post to #eng?" onConfirm={() => void send()} />
 * // Click Send → tooltip "Post to #eng?" [Confirm] next to the same Send button
 *
 * @example
 * <InlineSendConfirm hint="Send to ada@x.com?" onConfirm={send}>
 *   <AiActionButton label="Send" />
 * </InlineSendConfirm>
 */
const InlineSendConfirm = ({
  hint,
  disabled = false,
  loading = false,
  sendLabel = "Send",
  confirmLabel = "Confirm",
  loadingLabel = "Sending…",
  onArm,
  onConfirm,
  buttonClassName = "ai-text-action-btn ai-draft-expanded__recipient-update",
  children,
}) => {
  const [open, setOpen] = useState(false);
  const [tipStyle, setTipStyle] = useState(null);
  const rootRef = useRef(null);
  const tipRef = useRef(null);

  useEffect(() => {
    if (!hint) setOpen(false);
  }, [hint]);

  const updateTipPosition = useCallback(() => {
    setTipStyle(computeSendConfirmTipStyle(rootRef.current, tipRef.current));
  }, []);

  useLayoutEffect(() => {
    if (!open || loading || !hint) {
      setTipStyle(null);
      return undefined;
    }

    let resizeObserver = null;
    const observeTip = () => {
      const tipEl = tipRef.current;
      if (resizeObserver || !tipEl || typeof ResizeObserver === "undefined") return;
      resizeObserver = new ResizeObserver(() => updateTipPosition());
      resizeObserver.observe(tipEl);
    };

    updateTipPosition();
    observeTip();
    // Portal commit can lag one frame in Electron; remeasure once the tip is in the DOM.
    const frame = requestAnimationFrame(() => {
      updateTipPosition();
      observeTip();
    });
    window.addEventListener("resize", updateTipPosition);
    window.addEventListener("scroll", updateTipPosition, true);
    return () => {
      cancelAnimationFrame(frame);
      resizeObserver?.disconnect();
      window.removeEventListener("resize", updateTipPosition);
      window.removeEventListener("scroll", updateTipPosition, true);
    };
  }, [open, loading, hint, updateTipPosition]);

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (event) => {
      const target = event.target;
      if (rootRef.current?.contains(target) || tipRef.current?.contains(target)) return;
      setOpen(false);
    };
    const onKeyDown = (event) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  const armOrSend = () => {
    if (disabled || loading) return;
    if (!open) {
      if (onArm && onArm() === false) return;
      setOpen(true);
      return;
    }
    onConfirm?.();
  };

  const handleConfirm = (event) => {
    event.preventDefault();
    event.stopPropagation();
    onConfirm?.();
  };

  const trigger = children ? (
    React.cloneElement(React.Children.only(children), {
      onClick: (event) => {
        children.props.onClick?.(event);
        armOrSend();
      },
    })
  ) : (
    <button
      type="button"
      className={buttonClassName}
      onClick={armOrSend}
      disabled={disabled || loading}
      aria-expanded={open}
      aria-haspopup="dialog"
    >
      {loading ? loadingLabel : sendLabel}
    </button>
  );

  const showTip = open && !loading && Boolean(hint);
  const tipNode = showTip ? (
    <div
      ref={tipRef}
      className={[
        "ai-draft-expanded__send-confirm-tip",
        tipStyle?.openAbove
          ? "ai-draft-expanded__send-confirm-tip--above"
          : "ai-draft-expanded__send-confirm-tip--below",
      ].join(" ")}
      role="dialog"
      aria-label={hint}
      style={{
        top: tipStyle?.top ?? -9999,
        left: tipStyle?.left ?? -9999,
        visibility: tipStyle ? "visible" : "hidden",
      }}
      onMouseDown={(event) => event.stopPropagation()}
      onClick={(event) => event.stopPropagation()}
    >
      <p className="ai-draft-expanded__send-confirm-tip-copy">{hint}</p>
      <button
        type="button"
        className="ai-draft-expanded__send-confirm-tip-btn"
        onMouseDown={(event) => event.stopPropagation()}
        onClick={handleConfirm}
      >
        {confirmLabel}
      </button>
    </div>
  ) : null;

  return (
    <div className="ai-draft-expanded__send-confirm" ref={rootRef}>
      {trigger}
      {tipNode && typeof document !== "undefined"
        ? createPortal(tipNode, document.body)
        : null}
    </div>
  );
};

export default InlineSendConfirm;
